import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { environment } from '@environment';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { Maintenance, PagedResult } from '@interfaces';
import { merge, Observable } from 'rxjs';
import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import validator from 'validator';
import { DialogEditMaintenanceComponent } from '../../dialogs/dialog-edit-maintenance/dialog-maintenances.component';
import { MaintenanceFilterState } from '../../filter/services/maintenance-filter-state.service';

@Component({
  selector: 'datatable-maintenances',
  templateUrl: './datatable-maintenances.component.html',
  styleUrls: ['./datatable-maintenances.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatatableMaintenancesComponent implements OnInit, OnChanges {
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @Input() columns: any[];
  @Input() reload: EventEmitter<boolean>;
  @Input() hasEditMaintenancePermission: boolean;
  @Output() updatedValues = new EventEmitter<number>();

  dataSource: Array<Maintenance> = [];
  displayedColumns: string[];
  tableHeaderWidth: Object;
  isLoadingResults = true;
  resultsLength = 0;

  public readonly tabsToOpen = {
    general: 0,
    pendencies: 1,
    tasks: 2,
    forms: 3,
    quotations: 4,
    deadlines: 5,
    products: 6,
    services: 7,
    comments: 8,
    attachments: 9,
    logs: 10
  };

  public hasLabelViewPermission = this.authenticationService.hasPermission('labels-list');

  constructor(
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private maintenancesApi: MaintenancesApi,
    private authenticationService: AuthenticationService,
    private fuseProgressBarService: FuseProgressBarService,
    private filterMaintenancesService: MaintenanceFilterState,
  ) {
    this.displayedColumns = this.columns;
  }

  ngOnInit(): void {
    this.sort.active = 'openedAt';
    this.sort.direction = 'desc';

    merge(
      this.reload,
      this.paginator.page,
      this.sort.sortChange.pipe(tap(() => this.paginator.firstPage())),
      this.filterMaintenancesService.onChangeFilter.pipe(tap(() => this.paginator.firstPage())),
    )
      .pipe(
        startWith({}),
        debounceTime(500),
        switchMap(() => {
          this.isLoadingResults = true;
          this.fuseProgressBarService.show();
          return this.searchMaintenances(this.filterMaintenancesService.query);
        }),
        map((data) => {
          this.resultsLength = data.totalCount;
          return data.items;
        })
      )
      .subscribe((data) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.isLoadingResults = false;
        this.fuseProgressBarService.hide();
        this.updatedValues.emit(this.resultsLength);
        this.dataSource = data;
      });

    this.openEditDialogOnInit();
  }

  ngOnChanges(): void {
    this.displayedColumns = this.columns;
  }

  private async openEditDialogOnInit(): Promise<void> {
    const maintenanceId = this.route.snapshot.queryParamMap.get('id');
    const selectedTab = this.route.snapshot.queryParamMap.get('selectedTab');

    if (maintenanceId && validator.isUUID(maintenanceId)) {
      await this.editDialog({ id: maintenanceId } as Maintenance, !this.hasEditMaintenancePermission, this.tabsToOpen[selectedTab]);
    }
  }

  async editDialog(maintenance: Maintenance, isViewMode: boolean, selectedTabIndex: number = 0): Promise<void> {
    this.fuseProgressBarService.show();

    maintenance = await this.maintenancesApi.searchOne(maintenance.id).toPromise();
    this.fuseProgressBarService.hide();

    const editMaintenanceDialog = this.matDialog.open(DialogEditMaintenanceComponent, {
      panelClass: 'dialog-edit-maintenance',
      data: {
        maintenance,
        isViewMode,
        selectedTabIndex
      }
    });

    editMaintenanceDialog.afterClosed()
      .toPromise()
      .then((reload) => {
        if (reload) {
          this.reload.next(reload);
        }
      });

    editMaintenanceDialog.componentInstance.isLoading.subscribe((reload) => {
      this.reload.next(reload);
    });
  }

  searchMaintenances(query?): Observable<PagedResult> {
    const options = {
      orderColumn: this.sort.active,
      order: this.sort.direction,
      offset: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize
    };

    return this.maintenancesApi.searchWithWarranties(query, options);
  }

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value);
  }

  getPendencyTitles(pendencies): string[] {
    return pendencies.map((pendency) => pendency.title);
  }

  public redirectToPaymentsPage(paymentId: string): void {
    window.location.href = `${environment.MOUNTDOOM_2_URL}/#/pagamentos?id=${paymentId}`;
  }
}
